import Swal from 'sweetalert2';
import type { RadioEvent } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels, eventStatusColors,
  eventModalityLabels, eventModalityIcons, eventModalityColors,
  eventCategoryLabels, eventCategoryIcons, eventCategoryColors,
} from '@/mocks/adminEvents';

interface EventCardProps {
  event: RadioEvent;
  onEdit: (event: RadioEvent) => void;
  onDelete: (event: RadioEvent) => void;
  onViewDetail: (event: RadioEvent) => void;
  onShare: (event: RadioEvent) => void;
  onPublish: (event: RadioEvent) => void;
  onFinalize: (event: RadioEvent) => void;
  onCancelEvent: (event: RadioEvent) => void;
  onToggleHighlight: (event: RadioEvent) => void;
}

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function showActionsModal(
  event: RadioEvent,
  handlers: {
    onEdit: (e: RadioEvent) => void;
    onDelete: (e: RadioEvent) => void;
    onViewDetail: (e: RadioEvent) => void;
    onShare: (e: RadioEvent) => void;
    onPublish: (e: RadioEvent) => void;
    onFinalize: (e: RadioEvent) => void;
    onCancelEvent: (e: RadioEvent) => void;
    onToggleHighlight: (e: RadioEvent) => void;
  }
) {
  const isCanceled = event.status === 'cancelado';
  const isFinished = event.status === 'finalizado';
  const isBorrador = event.status === 'borrador';

  const actionBtn = (icon: string, label: string, color: string, hoverBg: string) =>
    `<button type="button" data-action="${label.toLowerCase().replace(/\s/g, '-')}"
      class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ${color} ${hoverBg}">
      <span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1">
        <i class="${icon} text-base" style="color:currentColor"></i>
      </span>
      ${label}
    </button>`;

  const actions: string[] = [];

  actions.push(actionBtn('ri-edit-line', 'Editar', 'text-foreground-700', 'hover:bg-background-100'));
  actions.push(actionBtn('ri-eye-line', 'Ver detalle', 'text-foreground-700', 'hover:bg-background-100'));

  if (!isCanceled && !isFinished) {
    actions.push(actionBtn(event.highlighted ? 'ri-star-fill' : 'ri-star-line', event.highlighted ? 'Quitar destacado' : 'Destacar', 'text-amber-600', 'hover:bg-amber-50'));
  }

  if (!isCanceled && !isFinished) {
    actions.push(actionBtn('ri-share-forward-line', 'Compartir', 'text-sky-600', 'hover:bg-sky-50'));
  }

  if (isBorrador) {
    actions.push(actionBtn('ri-send-plane-line', 'Publicar', 'text-emerald-600', 'hover:bg-emerald-50'));
  }

  if (!isCanceled && !isFinished && !isBorrador) {
    actions.push(actionBtn('ri-flag-line', 'Finalizar', 'text-foreground-600', 'hover:bg-foreground-100'));
  }

  if (!isCanceled && !isFinished) {
    actions.push(actionBtn('ri-close-circle-line', 'Cancelar', 'text-red-600', 'hover:bg-red-50'));
  }

  actions.push('<div class="border-t border-background-200 my-1"></div>');
  actions.push(actionBtn('ri-delete-bin-line', 'Eliminar', 'text-red-600', 'hover:bg-red-50'));

  sharedSwal
    .fire({
      title: event.title,
      html: `
        <div class="text-left space-y-1">
          <p class="text-xs text-foreground-500 mb-2 line-clamp-2">${escapeHtml(event.description)}</p>
          <div class="flex items-center gap-2 mb-3 flex-wrap">
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventTypeColors[event.type].bg} ${eventTypeColors[event.type].text} border ${eventTypeColors[event.type].border}">
              <i class="${eventTypeIcons[event.type]} text-[9px]"></i>${eventTypeLabels[event.type]}
            </span>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventModalityColors[event.modality].bg} ${eventModalityColors[event.modality].text} border ${eventModalityColors[event.modality].border}">
              <i class="${eventModalityIcons[event.modality]} text-[9px]"></i>${eventModalityLabels[event.modality]}
            </span>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventStatusColors[event.status].bg} ${eventStatusColors[event.status].text}">
              <span class="w-1.5 h-1.5 rounded-full ${eventStatusColors[event.status].dot}"></span>${eventStatusLabels[event.status]}
            </span>
          </div>
          ${actions.join('')}
        </div>
      `,
      customClass: {
        popup: 'rounded-2xl max-w-sm',
        title: 'font-bold text-sm',
        htmlContainer: '!p-0',
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (!popup) return;

        popup.querySelector('[data-action="editar"]')?.addEventListener('click', () => { Swal.close(); handlers.onEdit(event); });
        popup.querySelector('[data-action="ver-detalle"]')?.addEventListener('click', () => { Swal.close(); handlers.onViewDetail(event); });
        popup.querySelector('[data-action="compartir"]')?.addEventListener('click', () => { Swal.close(); handlers.onShare(event); });
        popup.querySelector('[data-action="publicar"]')?.addEventListener('click', () => { Swal.close(); handlers.onPublish(event); });
        popup.querySelector('[data-action="finalizar"]')?.addEventListener('click', () => { Swal.close(); handlers.onFinalize(event); });
        popup.querySelector('[data-action="cancelar"]')?.addEventListener('click', () => { Swal.close(); handlers.onCancelEvent(event); });
        popup.querySelector('[data-action="destacar"]')?.addEventListener('click', () => { Swal.close(); handlers.onToggleHighlight(event); });
        popup.querySelector('[data-action="quitar-destacado"]')?.addEventListener('click', () => { Swal.close(); handlers.onToggleHighlight(event); });
        popup.querySelector('[data-action="eliminar"]')?.addEventListener('click', () => { Swal.close(); handlers.onDelete(event); });
      },
    });
}

function formatMetric(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return k >= 10 ? Math.round(k).toLocaleString() + 'K' : k.toFixed(1) + 'K';
  }
  return n.toLocaleString();
}

export default function EventCard({
  event,
  onEdit,
  onDelete,
  onViewDetail,
  onShare,
  onPublish,
  onFinalize,
  onCancelEvent,
  onToggleHighlight,
}: EventCardProps) {
  const modalityStyle = eventModalityColors[event.modality];
  const statusStyle = eventStatusColors[event.status];
  const eventDate = new Date(event.date + 'T' + event.time);
  const now = new Date();
  const daysLeft = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isCanceled = event.status === 'cancelado';
  const isFinished = event.status === 'finalizado';
  const isBorrador = event.status === 'borrador';
  const hasCapacity = event.capacity > 0;
  const capacityPercent = hasCapacity ? Math.round((event.participants / event.capacity) * 100) : 0;
  const capacityFull = hasCapacity && event.participants >= event.capacity;

  const metrics = [
    { icon: 'ri-user-fill', label: 'Asistentes', value: formatMetric(event.participants), color: 'text-foreground-900' },
    { icon: 'ri-heart-fill', label: 'Interesados', value: formatMetric(event.interested), color: 'text-rose-600' },
    { icon: 'ri-share-forward-fill', label: 'Compartidos', value: formatMetric(event.shares), color: 'text-sky-600' },
    { icon: 'ri-user-follow-fill', label: 'Seguidores', value: formatMetric(event.followers), color: 'text-emerald-600' },
  ];

  return (
    <div className={`rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden transition-all hover:border-background-300/80 group ${isCanceled ? 'opacity-70' : ''}`}>
      {/* Modality accent bar */}
      <div className={`h-1 ${modalityStyle.dot}`}></div>

      {/* Image */}
      <div className="relative h-36 overflow-hidden">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>

        {/* Modality badge */}
        <div className={`absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${modalityStyle.bg} ${modalityStyle.text} border ${modalityStyle.border} backdrop-blur-sm`}>
          <span className={`w-1.5 h-1.5 rounded-full ${modalityStyle.dot}`}></span>
          {eventModalityLabels[event.modality]}
        </div>

        {/* Highlighted star */}
        {event.highlighted && (
          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
            <i className="ri-star-fill text-white text-[10px]"></i>
          </div>
        )}

        {/* Status overlay */}
        {isBorrador && (
          <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded-md bg-foreground-900/70 text-white text-[9px] font-semibold backdrop-blur-sm">
            Borrador
          </div>
        )}
        {isCanceled && (
          <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded-md bg-red-500/80 text-white text-[9px] font-semibold backdrop-blur-sm">
            Cancelado
          </div>
        )}
        {isFinished && (
          <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded-md bg-foreground-500/70 text-white text-[9px] font-semibold backdrop-blur-sm">
            Finalizado
          </div>
        )}
      </div>

      <div className="p-4">
        {/* Title + Actions */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventTypeColors[event.type].bg} ${eventTypeColors[event.type].text} border ${eventTypeColors[event.type].border}`}>
                <i className={`${eventTypeIcons[event.type]} text-[10px]`}></i>
                {eventTypeLabels[event.type]}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${eventCategoryColors[event.category].bg} ${eventCategoryColors[event.category].text} border ${eventCategoryColors[event.category].border}`}>
                <i className={`${eventCategoryIcons[event.category]} text-[10px]`}></i>
                {eventCategoryLabels[event.category]}
              </span>
            </div>
            <h3 className="text-foreground-950 font-bold text-sm leading-tight">{event.title}</h3>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              showActionsModal(event, { onEdit, onDelete, onViewDetail, onShare, onPublish, onFinalize, onCancelEvent, onToggleHighlight });
            }}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
            title="Acciones"
          >
            <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Date + Location */}
        <div className="space-y-1 mb-3">
          <div className="flex items-center gap-1.5 text-foreground-500 text-[11px]">
            <i className="ri-calendar-line text-[10px] text-foreground-400"></i>
            <span>
              {eventDate.toLocaleDateString('es-MX', { weekday: 'short', month: 'short', day: 'numeric' })} · {event.time} hs
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-foreground-500 text-[11px]">
            <i className={`${eventModalityIcons[event.modality]} text-[10px] text-foreground-400`}></i>
            <span className="truncate">{event.location}</span>
          </div>
        </div>

        {/* Capacity bar */}
        {hasCapacity && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-foreground-500 font-medium">Cupo</span>
              <span className={`font-bold ${capacityFull ? 'text-red-600' : capacityPercent >= 80 ? 'text-amber-600' : 'text-foreground-600'}`}>
                {event.participants.toLocaleString()} / {event.capacity.toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${capacityFull ? 'bg-red-400' : capacityPercent >= 80 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                style={{ width: `${Math.min(100, capacityPercent)}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* 4 Metrics */}
        <div className="grid grid-cols-4 gap-2 pt-3 border-t border-background-200/60">
          {metrics.map((m) => (
            <div key={m.label} className="text-center">
              <div className="flex items-center justify-center gap-0.5 mb-0.5">
                <i className={`${m.icon} text-[10px] ${m.color}`}></i>
                <span className={`text-xs font-bold ${m.color}`}>{m.value}</span>
              </div>
              <p className="text-foreground-400 text-[9px]">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Status dot + days left */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-background-200/40">
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${statusStyle.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}></span>
            {eventStatusLabels[event.status]}
          </span>

          {event.status === 'publicado' && daysLeft > 0 && (
            <span className="text-emerald-600 text-[10px] font-bold flex items-center gap-1">
              <i className="ri-time-line text-[9px]"></i>
              {daysLeft > 0 ? `${daysLeft} día${daysLeft !== 1 ? 's' : ''}` : '¡Es hoy!'}
            </span>
          )}

          {isBorrador && daysLeft > 0 && (
            <span className="text-foreground-400 text-[10px] font-medium flex items-center gap-1">
              <i className="ri-time-line text-[9px]"></i>
              {daysLeft > 0 ? `${daysLeft} día${daysLeft !== 1 ? 's' : ''}` : 'Hoy'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}